import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import MDEditor from '@uiw/react-md-editor';
import Layout from '../components/Layout';
import RewriteModal from '../components/RewriteModal';

// ─── API helpers ───────────────────────────────────────────────────────────────

const api = axios.create({ baseURL: '/api/gdriveeditor', headers: { 'Content-Type': 'application/json' } });

const documentService = {
  async loadFromUrl(url: string) {
    const response = await api.get('/documents', { params: { url } });
    return response.data;
  },
  async getDocument(docId: string, format = 'markdown') {
    const response = await api.get(`/documents/${docId}`, { params: { format } });
    return response.data;
  },
  async updateDocument(docId: string, content: string) {
    const response = await api.put(`/documents/${docId}`, { content });
    return response.data;
  },
  async downloadDocument(docId: string, format = 'md') {
    return api.get(`/documents/${docId}/download`, { params: { format }, responseType: 'blob' });
  },
  async downloadAll(documentIds: string[], format = 'md') {
    return api.post('/documents/download-all', { document_ids: documentIds, format }, { responseType: 'blob' });
  },
};

function downloadFile(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// ─── Types ─────────────────────────────────────────────────────────────────────

interface GDoc {
  id: string;
  name: string;
  modifiedTime?: string;
  createdTime?: string;
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function URLInput({ onLoad, isLoading }: { onLoad: (url: string) => void; isLoading: boolean }) {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim()) { setError('Please enter a Google Drive URL'); return; }
    const valid = [/drive\.google\.com\/drive\/folders\//, /docs\.google\.com\/document\/d\//, /drive\.google\.com\/file\/d\//];
    if (!valid.some(p => p.test(url)) && !/^[a-zA-Z0-9_-]+$/.test(url)) {
      setError('Please enter a valid Google Drive folder or document URL'); return;
    }
    setError('');
    onLoad(url);
  };

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="flex gap-2">
        <div className="flex-1">
          <input type="text" value={url} onChange={e => { setUrl(e.target.value); setError(''); }}
            placeholder="Paste Google Drive folder or document URL..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={isLoading} />
          {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
        </div>
        <button type="submit" disabled={isLoading}
          className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          {isLoading ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
              Loading...
            </span>
          ) : 'Load'}
        </button>
      </div>
    </form>
  );
}

function DocumentList({ documents, selectedDoc, onSelectDoc, isLoading }: {
  documents: GDoc[]; selectedDoc: GDoc | null; onSelectDoc: (doc: GDoc) => void; isLoading: boolean;
}) {
  const formatDate = (ds?: string) => {
    if (!ds) return '';
    return new Date(ds).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (documents.length === 0) {
    return (
      <div className="p-4 text-gray-500 text-center">
        <svg className="w-12 h-12 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p className="text-sm">No documents loaded</p>
        <p className="text-xs mt-1">Enter a Google Drive URL above</p>
      </div>
    );
  }

  return (
    <div className="overflow-y-auto">
      <div className="p-3 border-b border-gray-200 bg-gray-50">
        <h3 className="font-medium text-gray-700 text-sm">Documents ({documents.length})</h3>
      </div>
      <ul className="divide-y divide-gray-100">
        {documents.map(doc => (
          <li key={doc.id}>
            <button onClick={() => onSelectDoc(doc)} disabled={isLoading}
              className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors disabled:opacity-50 ${selectedDoc?.id === doc.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}>
              <div className="flex items-start gap-3">
                <svg className={`w-5 h-5 mt-0.5 flex-shrink-0 ${selectedDoc?.id === doc.id ? 'text-blue-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <div className="min-w-0 flex-1">
                  <p className={`font-medium truncate ${selectedDoc?.id === doc.id ? 'text-blue-700' : 'text-gray-800'}`}>{doc.name}</p>
                  {doc.modifiedTime && <p className="text-xs text-gray-500 mt-0.5">Modified {formatDate(doc.modifiedTime)}</p>}
                </div>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────

export default function GDriveEditorPage() {
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  // Support both new URL structure (/projects/:projectId/agent) and legacy (?project_id=...)
  const projectId = urlProjectId || searchParams.get('project_id') ? Number(urlProjectId || searchParams.get('project_id')) : null;

  const [documents, setDocuments] = useState<GDoc[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<GDoc | null>(null);
  const [content, setContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [isLoadingDocs, setIsLoadingDocs] = useState(false);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Undo/redo history for rewrites
  const [undoStack, setUndoStack] = useState<string[]>([]);
  const [redoStack, setRedoStack] = useState<string[]>([]);

  // Rewrite modal state
  const [selectedText, setSelectedText] = useState('');
  const [showRewriteModal, setShowRewriteModal] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);

  const showSuccess = (msg: string) => { setSuccessMessage(msg); setTimeout(() => setSuccessMessage(''), 3000); };

  // Text selection
  useEffect(() => {
    const handle = () => {
      const sel = window.getSelection();
      if (sel && sel.toString().trim()) {
        setSelectedText(sel.toString());
      }
    };
    document.addEventListener('mouseup', handle);
    document.addEventListener('keyup', handle);
    return () => { document.removeEventListener('mouseup', handle); document.removeEventListener('keyup', handle); };
  }, [content]);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleLoadDocuments = useCallback(async (url: string) => {
    setIsLoadingDocs(true); setError(''); setSelectedDoc(null); setContent(''); setOriginalContent('');
    try {
      const result = await documentService.loadFromUrl(url);
      setDocuments(result.documents || []);
      if (result.documents?.length === 0) setError('No documents found at this URL');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load documents'); setDocuments([]);
    } finally { setIsLoadingDocs(false); }
  }, []);

  const handleSelectDocument = useCallback(async (doc: GDoc) => {
    if (selectedDoc?.id === doc.id) return;
    setIsLoadingContent(true); setError(''); setSelectedDoc(doc);
    setUndoStack([]); setRedoStack([]);
    try {
      const result = await documentService.getDocument(doc.id);
      setContent(result.content || ''); setOriginalContent(result.content || '');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load document content'); setContent(''); setOriginalContent('');
    } finally { setIsLoadingContent(false); }
  }, [selectedDoc]);

  const handleSave = useCallback(async () => {
    if (!selectedDoc) return;
    setIsSaving(true); setError('');
    try {
      await documentService.updateDocument(selectedDoc.id, content);
      setOriginalContent(content); showSuccess('Document saved successfully!');
    } catch (err: any) { setError(err.response?.data?.detail || 'Failed to save document'); }
    finally { setIsSaving(false); }
  }, [selectedDoc, content]);

  const handleDownload = useCallback(async () => {
    if (!selectedDoc) return;
    try {
      const response = await documentService.downloadDocument(selectedDoc.id, 'md');
      downloadFile(response.data, `${selectedDoc.name}.md`);
    } catch (err: any) { setError(err.response?.data?.detail || 'Failed to download document'); }
  }, [selectedDoc]);

  const handleDownloadAll = useCallback(async () => {
    if (documents.length === 0) return;
    try {
      const response = await documentService.downloadAll(documents.map(d => d.id), 'md');
      downloadFile(response.data, 'documents.zip');
    } catch (err: any) { setError(err.response?.data?.detail || 'Failed to download documents'); }
  }, [documents]);

  const handleRewriteAccept = (rewrittenText: string) => {
    setUndoStack(prev => [...prev, content]);
    setRedoStack([]);
    setContent(prev => prev.replace(selectedText, rewrittenText));
    setShowRewriteModal(false); setSelectedText('');
    showSuccess('Rewrite applied!');
  };

  const handleUndoRewrite = () => {
    if (undoStack.length === 0) return;
    const prev = undoStack[undoStack.length - 1];
    setRedoStack(r => [...r, content]);
    setUndoStack(u => u.slice(0, -1));
    setContent(prev);
    showSuccess('Rewrite undone');
  };

  const handleRedoRewrite = () => {
    if (redoStack.length === 0) return;
    const next = redoStack[redoStack.length - 1];
    setUndoStack(u => [...u, content]);
    setRedoStack(r => r.slice(0, -1));
    setContent(next);
    showSuccess('Rewrite redone');
  };

  const hasChanges = content !== originalContent;

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <Layout projectId={projectId}>
      <div className="flex flex-col h-[calc(100vh-7rem)]">
        {/* Header bar */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h1 className="text-xl font-bold text-gray-800">Google Drive Document Editor</h1>
            <a href="https://drive.google.com/file/d/1YkU_qoT7Kcvwtq3yjk9eIeVB_j5OcEMo/view" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-brand-600 hover:text-brand-700 font-medium">
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" /></svg>
              Video Tutorial
            </a>
          </div>
          <div className="w-1/2">
            <URLInput onLoad={handleLoadDocuments} isLoading={isLoadingDocs} />
          </div>
        </div>

        {/* Notifications */}
        {error && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
            <span className="text-sm">{error}</span>
            <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
            </button>
          </div>
        )}
        {successMessage && (
          <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
            <span className="text-sm">{successMessage}</span>
          </div>
        )}

        {/* Main content area */}
        <div className="flex-1 flex overflow-hidden gap-4">
          {/* Sidebar - Document List */}
          <aside className="w-72 flex-shrink-0 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <DocumentList documents={documents} selectedDoc={selectedDoc} onSelectDoc={handleSelectDocument} isLoading={isLoadingContent} />
          </aside>

          {/* Editor panel */}
          <section className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col" ref={editorRef}>
            {!selectedDoc ? (
              <div className="h-full flex items-center justify-center bg-gray-50">
                <div className="text-center text-gray-500">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <p className="text-lg font-medium">Select a document to edit</p>
                  <p className="text-sm mt-1">Choose a document from the sidebar to start editing</p>
                </div>
              </div>
            ) : (
              <>
                {/* Toolbar */}
                <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    <h2 className="font-semibold text-gray-800 truncate max-w-md">{selectedDoc.name}</h2>
                    {hasChanges && <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded">Unsaved</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => { if (selectedText.trim()) setShowRewriteModal(true); }}
                      disabled={!selectedText.trim()}
                      className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title={selectedText.trim() ? 'Rewrite selected text' : 'Select text to rewrite'}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                      Rewrite
                    </button>
                    {/* Undo / Redo rewrite */}
                    <button onClick={handleUndoRewrite} disabled={undoStack.length === 0}
                      className="flex items-center gap-1.5 px-3 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      title={undoStack.length > 0 ? `Undo rewrite (${undoStack.length})` : 'No rewrites to undo'}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a5 5 0 015 5v2M3 10l4-4M3 10l4 4" /></svg>
                      Undo
                    </button>
                    <button onClick={handleRedoRewrite} disabled={redoStack.length === 0}
                      className="flex items-center gap-1.5 px-3 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      title={redoStack.length > 0 ? `Redo rewrite (${redoStack.length})` : 'No rewrites to redo'}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10H11a5 5 0 00-5 5v2M21 10l-4-4M21 10l-4 4" /></svg>
                      Redo
                    </button>
                    <button onClick={handleSave} disabled={isSaving || !hasChanges}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                      {isSaving ? (
                        <><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>Saving...</>
                      ) : (
                        <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg>Save to Drive</>
                      )}
                    </button>
                    <div className="h-6 w-px bg-gray-300" />
                    <button onClick={handleDownload} disabled={isLoadingContent}
                      className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                      Download
                    </button>
                    {documents.length > 1 && (
                      <button onClick={handleDownloadAll} disabled={isLoadingContent}
                        className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>
                        Download All
                      </button>
                    )}
                  </div>
                </div>

                {/* Selection indicator */}
                {selectedText.trim() && (
                  <div className="px-4 py-2 bg-purple-50 border-b border-purple-100 flex items-center justify-between">
                    <span className="text-sm text-purple-700"><span className="font-medium">{selectedText.length}</span> characters selected</span>
                    <button onClick={() => setSelectedText('')} className="text-sm text-purple-600 hover:text-purple-800">Clear selection</button>
                  </div>
                )}

                {/* Editor */}
                <div className="flex-1 overflow-hidden" data-color-mode="light">
                  {isLoadingContent ? (
                    <div className="h-full flex items-center justify-center bg-gray-50">
                      <div className="text-center">
                        <svg className="animate-spin h-8 w-8 mx-auto text-blue-500" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                        <p className="mt-2 text-gray-500">Loading document...</p>
                      </div>
                    </div>
                  ) : (
                    <MDEditor value={content} onChange={(v) => setContent(v || '')} height="100%" preview="live" visibleDragbar={false} />
                  )}
                </div>
              </>
            )}
          </section>
        </div>

        {/* ─── Rewrite Modal ──────────────────────────────────────────────── */}
        <RewriteModal
          isOpen={showRewriteModal}
          selectedText={selectedText}
          onClose={() => { setShowRewriteModal(false); setSelectedText(''); }}
          onAccept={handleRewriteAccept}
        />
      </div>
    </Layout>
  );
}
